import { useFormik } from "formik";
import { DrawerClose } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import {
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import {
  useUpdateFinanceAccountMutation,
  useFetchBanksFinanceAccountsQuery,
  useFetchTypeFinanceAccountsQuery,
  useToast,
  useFetchFinanceAccountByIdQuery,
  usePermission,
} from "@/hooks";
import { FinanceAccount, UpdateFinanceAccountRequestDTO } from "@/types";
import { FinanceAccountValidationSchema } from "@/shared";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "@/components/ui/select";
import { MessageError } from "../../message-error";
import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { defaultFinancialAccounts } from "@/helpers";
import { DisplayError } from "@/utils";

type Props = {
  id: string;
  handleClose: () => void;
};

export function FormEditFinanceAccount({ id, handleClose }: Props) {
  const { t } = useTranslation();
  const { checkPermission } = usePermission();
  const { toast } = useToast();

  const { data: accountData, isLoading: isLoadingAccount } =
    useFetchFinanceAccountByIdQuery(id);
  const { mutateAsync, isLoading } = useUpdateFinanceAccountMutation();
  const [account, setAccount] = useState<FinanceAccount | undefined>(
    accountData
  );

  const initialValues = {
    id: account?.id || "",
    description: defaultFinancialAccounts.includes(account?.description || "")
      ? account
        ? t(account.description.toLowerCase())
        : ""
      : account?.description || "",
    number: account?.number || "",
    agency: account?.agency || "",
    comments: account?.comments || "",
    bankId: account?.bank?.id || "",
    typeId: account?.type?.id || "",
    shared: account?.shared || 0,
    is_default: account?.is_default || 0,
    owner: "",
  };

  const { data: dataTypeAccounts, isLoading: isLoadingTypeAccounts } =
    useFetchTypeFinanceAccountsQuery();
  const { data: dataBanks, isLoading: isLoadingBanks } =
    useFetchBanksFinanceAccountsQuery();

  const { handleSubmit, getFieldProps, errors, touched, ...formik } =
    useFormik<UpdateFinanceAccountRequestDTO>({
      initialValues,
      validationSchema: FinanceAccountValidationSchema(),
      onSubmit: async (accountData) => {
        await updateFinanceAccount(accountData);
      },
    });

  async function updateFinanceAccount(
    accountData: UpdateFinanceAccountRequestDTO
  ) {
    try {
      await mutateAsync(accountData, {
        onSuccess: ({ message }) => {
          toast({
            title: t("LABEL.success"),
            description: t(`MESSAGE.${message}`),
          });
          handleClose();
        },

        onError: (error: any) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  useEffect(() => {
    if (id && accountData) {
      setAccount(accountData);
      formik.setValues({
        id: accountData?.id || "",
        description: defaultFinancialAccounts.includes(
          accountData?.description || ""
        )
          ? accountData
            ? t(accountData.description.toLowerCase())
            : ""
          : accountData?.description || "",
        number: accountData?.number || "",
        agency: accountData?.agency || "",
        comments: accountData?.comments || "",
        bankId: accountData?.bank?.id || "",
        typeId: accountData?.type?.id || "",
        shared: accountData?.shared || 0,
        is_default: accountData?.is_default || 0,
        owner: "",
      });
    }

  }, [id, accountData]);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="h-full overflow-y-scroll flex flex-col">
        <DrawerHeader className="flex-none">
          <DrawerTitle className="text-teal-700 font-bold">
            Editar conta
          </DrawerTitle>
          <DrawerDescription>
            Preencha os campos para editar a conta financeira.
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {!account && isLoadingAccount ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <Label>Nome</Label>
                <div className="space-y-1">
                  <Input
                    isInvalid={touched.description && errors.description}
                    isValid={touched.description && !errors.description}
                    {...getFieldProps("description")}
                  />
                  {touched.description && errors.description ? (
                    <span className="text-red-500 text-sm min-h-3">
                      {errors.description}
                    </span>
                  ) : (
                    <div className="min-h-3"></div>
                  )}
                </div>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-5">
            {!account && isLoadingAccount ? (
              Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <Label>Tipo</Label>
                  <div className="space-y-1">
                    <Select
                      {...getFieldProps("typeId")}
                      onValueChange={(value) => {
                        formik.setFieldValue("typeId", value);
                      }}
                      defaultValue={initialValues.typeId}
                    >
                      <SelectTrigger
                        isInvalid={
                          touched.typeId && errors.typeId !== undefined
                        }
                        isValid={touched.typeId && errors.typeId === undefined}
                        loading={isLoadingTypeAccounts || !dataTypeAccounts}
                      >
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {dataTypeAccounts &&
                            Array.isArray(dataTypeAccounts) &&
                            dataTypeAccounts.map((type) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.name}
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <MessageError messageError={errors.typeId} />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label>Visibilidade</Label>
                  <div className="space-y-1">
                    <Select
                      {...getFieldProps("shared")}
                      onValueChange={(value) => {
                        formik.setFieldValue("shared", value);
                      }}
                      defaultValue={initialValues.shared.toString()}
                    >
                      <SelectTrigger
                        isInvalid={touched.shared && errors.shared}
                        isValid={touched.shared && !errors.shared}
                      >
                        <SelectValue placeholder="Selecione a visibilidade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="0">Pública</SelectItem>
                          <SelectItem value="1">Privada</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <MessageError messageError={errors.shared} />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label>Banco</Label>
                  <div className="space-y-1">
                    <Select
                      {...getFieldProps("bankId")}
                      onValueChange={(value) => {
                        formik.setFieldValue("bankId", value);
                      }}
                      defaultValue={initialValues.bankId}
                    >
                      <SelectTrigger
                        isInvalid={
                          touched.bankId && errors.bankId !== undefined
                        }
                        isValid={touched.bankId && errors.bankId === undefined}
                        loading={isLoadingBanks || !dataBanks}
                      >
                        <SelectValue placeholder="Selecione o banco" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {dataBanks &&
                            Array.isArray(dataBanks) &&
                            dataBanks.map((bank) => (
                              <SelectItem key={bank.id} value={bank.id}>
                                {bank.name}
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <MessageError messageError={errors.bankId} />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label>Padrão</Label>
                  <div className="space-y-1">
                    <Select
                      {...getFieldProps("is_default")}
                      onValueChange={(value) => {
                        formik.setFieldValue("is_default", value);
                      }}
                      defaultValue={initialValues.is_default.toString()}
                    >
                      <SelectTrigger
                        isInvalid={touched.is_default && errors.is_default}
                        isValid={touched.is_default && !errors.is_default}
                      >
                        <SelectValue placeholder="Selecione se é padrão" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="0">Não</SelectItem>
                          <SelectItem value="1">Sim</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <MessageError messageError={errors.is_default} />
                  </div>
                </div>
              </>
            )}
          </div>
          {!account && isLoadingAccount ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <div className="flex flex-col gap-2">
              <Label>Descrição</Label>
              <Textarea
                placeholder="Descrição"
                isInvalid={touched.comments && errors.comments}
                isValid={touched.comments && !errors.comments}
                {...getFieldProps("comments")}
                messageError={errors.comments}
              />
            </div>
          )}
        </div>

        <DrawerFooter className=" flex flex-row justify-center mb-5  pb-10 gap-2">
          <Button
            type="submit"
            className="px-10 "
            disabled={!checkPermission("finance account", "update finance account") || isLoading || !formik.isValid}
            isLoading={isLoading}
          >
            {t("LABEL.save")}
          </Button>
          <DrawerClose>
            <Button variant="outline">Cancelar</Button>
          </DrawerClose>
        </DrawerFooter>
      </div>
    </form>
  );
}
